import { trackButtonClick } from '@/utils/buttonTracking'
import {
  FieldKey,
  PlanATripSelectionOption,
  PlanATripMessage,
  RecommendedCountryDetails,
} from '@/types/chat/PlanATripMessage'
import { MonthNames } from '@/constants'

const ANALYTICS_LOCATION = 'plan_a_trip_chat'
const RECOMMENDED_SECTION = 'recommended_country'
const DATE_SECTION = 'duration_and_dates'

interface TrackOptionSelectionParams {
  messageId: PlanATripMessage['id']
  selections: string[]
  options: Array<PlanATripSelectionOption & { text: string }>
  targetField?: FieldKey | undefined
  itineraryRequestId?: number | string | null
}

interface TrackRecommendedSelectionParams {
  messageId: PlanATripMessage['id']
  selections: string[]
  options: RecommendedCountryDetails[]
  itineraryRequestId?: number | string | null
}

interface TrackDateSelectionParams {
  messageId: PlanATripMessage['id']
  months: { year: number; month: number }[]
  duration: number
  durationUnit: string
  dateType: string
  itineraryRequestId?: number | string | null
}

export function trackPlanATripOptionSelections({
  messageId,
  selections,
  options,
  targetField,
  itineraryRequestId,
}: TrackOptionSelectionParams) {
  selections.forEach(selection => {
    const option = options.find(opt => opt.selection === selection)
    if (!option) {
      return
    }

    const metadata: Record<string, unknown> = {
      plan_a_trip_message_id: messageId,
    }

    if (targetField) {
      metadata.plan_a_trip_target_field = targetField
    }

    if (itineraryRequestId) {
      metadata.plan_a_trip_itinerary_request_id = itineraryRequestId
    }

    trackButtonClick({
      label: option.text,
      value: option.selection,
      location: ANALYTICS_LOCATION,
      section: targetField ?? undefined,
      metadata,
    })
  })
}

export function trackPlanATripRecommendedSelections({
  messageId,
  selections,
  options,
  itineraryRequestId,
}: TrackRecommendedSelectionParams) {
  selections.forEach(selection => {
    const option = options.find(opt => opt.selection === selection)
    if (!option) {
      return
    }

    const metadata: Record<string, unknown> = {
      plan_a_trip_message_id: messageId,
      plan_a_trip_target_field: option.targetField,
    }

    if (itineraryRequestId) {
      metadata.plan_a_trip_itinerary_request_id = itineraryRequestId
    }

    if (option.mostRecommended) {
      metadata.plan_a_trip_recommendation_status = 'most_recommended'
    }

    trackButtonClick({
      label: option.text,
      value: option.selection,
      location: ANALYTICS_LOCATION,
      section: RECOMMENDED_SECTION,
      metadata,
    })
  })
}

export function trackPlanATripDateSelection({
  messageId,
  months,
  duration,
  durationUnit,
  dateType,
  itineraryRequestId,
}: TrackDateSelectionParams) {
  const formattedMonths = months.map(({ month, year }) => `${MonthNames[month].short} ${year}`)

  formattedMonths.forEach(monthLabel => {
    const metadata: Record<string, unknown> = {
      plan_a_trip_message_id: messageId,
      plan_a_trip_target_field: DATE_SECTION,
      plan_a_trip_date_type: dateType,
      plan_a_trip_duration_value: duration,
      plan_a_trip_duration_unit: durationUnit,
      plan_a_trip_month: monthLabel,
      plan_a_trip_months: formattedMonths,
    }

    if (itineraryRequestId) {
      metadata.plan_a_trip_itinerary_request_id = itineraryRequestId
    }

    trackButtonClick({
      label: 'Dates selection submitted',
      value: monthLabel,
      location: ANALYTICS_LOCATION,
      section: DATE_SECTION,
      metadata,
    })
  })
}
